import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  simpleBugoBrandListDataAtom,
  simpleBugoBrandListStatusAtom,
} from '@shared/api/bugoBrand/bugoBrand.atom';
import { useSimpleBugoBrandListHook } from '@shared/api/bugoBrand/bugoBrand.hook';
import { BugoBrand } from '@shared/api/bugoBrand/bugoBrand.interface';
import {
  giftItemListDataAtom,
  giftItemListStatusAtom,
} from '@shared/api/giftItem/giftItem.atom';
import {
  bulkCreateGiftItems,
  createGiftItem,
} from '@shared/api/giftItem/giftItem.controller';
import { useGiftItemListHook } from '@shared/api/giftItem/giftItem.hook';
import {
  GiftItemBulkCreateItem,
  GiftItemBulkSkipReason,
  GiftItemCreate,
} from '@shared/api/giftItem/giftItem.interface';
import {
  giftItemInfoListDataAtom,
  giftItemInfoListStatusAtom,
} from '@shared/api/giftItemInfo/giftItemInfo.atom';
import { useGiftItemInfoListHook } from '@shared/api/giftItemInfo/giftItemInfo.hook';
import { GiftItemInfoData } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Loading } from '@shared/components/Loading';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import axios from 'axios';
import { useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';

import styles from '../components/GiftBrandManagement.module.css';

//* 브랜드(공급사) 단위 일괄 등록.
//* 원상품(gift-item-info)을 상품 브랜드별로 묶어 보여주고, 체크한 것만 상조에 한 번에 등록한다.
//* 서버 일괄 API(POST gift-item/bulk) 한 번으로 등록하고, 서버가 건너뛴 행(중복·미존재·판매중지)은 행별로 표시.
//* 구서버(404)면 단건 create 순차 호출로 폴백.

const NO_BRAND_KEY = '__none__';

type RowEdit = {
  checked: boolean;
  priceRegular: number;
  priceRetail: number;
  priceWhole: number;
  priceRebate: number;
  isTaxable: boolean;
};

type RowResult = { status: 'pending' | 'success' | 'error'; message?: string };

const SKIP_REASON_LABEL: Record<GiftItemBulkSkipReason, string> = {
  Duplicate: '이미 등록된 상품',
  NotFound: '원상품 없음',
  Disabled: '판매중지 원상품',
};

const PRICE_LABELS: Partial<Record<keyof RowEdit, string>> = {
  priceRegular: '정가',
  priceRetail: '판매가',
  priceWhole: '도매가',
  priceRebate: '리베이트',
};

const NUMBER_FIELDS: (keyof RowEdit)[] = [
  'priceRegular',
  'priceRetail',
  'priceWhole',
  'priceRebate',
];

const rowFromInfo = (info: GiftItemInfoData, checked: boolean): RowEdit => ({
  checked,
  priceRegular: info.priceRegular || 0,
  priceRetail: info.priceRetail || 0,
  priceWhole: info.priceWhole || 0,
  priceRebate: 0,
  isTaxable: info.isTaxable ?? false,
});

const brandKeyOf = (info: GiftItemInfoData) =>
  typeof info.giftItemBrand === 'object' && info.giftItemBrand
    ? info.giftItemBrand._id
    : typeof info.giftItemBrand === 'string' && info.giftItemBrand
    ? info.giftItemBrand
    : NO_BRAND_KEY;

export const GiftItemBulkFormPage: FC = function GiftItemBulkFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { sellerRole } = useAuth();
  const role = sellerRole();

  useGiftItemInfoListHook(role);
  useSimpleBugoBrandListHook();
  useGiftItemListHook(role); // 중복 체크용

  const giftItemInfoListStatus = useRecoilValue(giftItemInfoListStatusAtom);
  const giftItemInfoList = useRecoilValue(giftItemInfoListDataAtom);
  const bugoBrandListStatus = useRecoilValue(simpleBugoBrandListStatusAtom);
  const bugoBrandList = useRecoilValue(simpleBugoBrandListDataAtom);
  const giftItemListStatus = useRecoilValue(giftItemListStatusAtom);
  const giftItemListData = useRecoilValue(giftItemListDataAtom);

  const [bugoBrandId, setBugoBrandId] = useState('');
  const [itemBrandKey, setItemBrandKey] = useState('');
  const [rows, setRows] = useState<Record<string, RowEdit>>({});
  const [results, setResults] = useState<Record<string, RowResult>>({});
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // 판매 가능한 원상품만 (단건 등록 폼과 동일 기준)
  const enabledInfos = useMemo(
    () =>
      giftItemInfoListStatus === 'success' && giftItemInfoList
        ? giftItemInfoList.filter((info: GiftItemInfoData) => info.isEnabled)
        : [],
    [giftItemInfoListStatus, giftItemInfoList],
  );

  // 상품 브랜드 목록 (원상품에 실제로 쓰인 브랜드만)
  const itemBrandOptions = useMemo(() => {
    const map = new Map<string, { name: string; count: number }>();
    enabledInfos.forEach((info) => {
      const key = brandKeyOf(info);
      const name =
        key === NO_BRAND_KEY
          ? '(브랜드 미지정)'
          : typeof info.giftItemBrand === 'object' && info.giftItemBrand?.name
          ? info.giftItemBrand.name
          : key;
      const prev = map.get(key);
      map.set(key, { name, count: (prev?.count ?? 0) + 1 });
    });
    return Array.from(map.entries())
      .map(([key, v]) => ({ key, ...v }))
      .sort((a, b) =>
        a.key === NO_BRAND_KEY
          ? 1
          : b.key === NO_BRAND_KEY
          ? -1
          : a.name.localeCompare(b.name),
      );
  }, [enabledInfos]);

  // 선택 브랜드의 원상품 (표시 순서 → 이름순)
  const brandInfos = useMemo(() => {
    if (!itemBrandKey) return [];
    return enabledInfos
      .filter((info) => brandKeyOf(info) === itemBrandKey)
      .sort((a, b) => {
        const ao = a.order ?? Number.MAX_SAFE_INTEGER;
        const bo = b.order ?? Number.MAX_SAFE_INTEGER;
        if (ao !== bo) return ao - bo;
        return a.name.localeCompare(b.name);
      });
  }, [enabledInfos, itemBrandKey]);

  // 선택 상조에 이미 등록된 원상품 id 집합
  const registeredInfoIds = useMemo(() => {
    const set = new Set<string>();
    if (!bugoBrandId || giftItemListStatus !== 'success' || !giftItemListData) return set;
    giftItemListData.forEach((item) => {
      const brandId =
        typeof item.bugoBrand === 'object' ? item.bugoBrand?._id : item.bugoBrand;
      if (brandId !== bugoBrandId) return;
      const infoId =
        typeof item.giftItemInfo === 'object'
          ? item.giftItemInfo?._id
          : item.giftItemInfo;
      if (infoId) set.add(infoId);
    });
    return set;
  }, [bugoBrandId, giftItemListStatus, giftItemListData]);

  // 행 편집값 시딩. 브랜드가 바뀌면 전부 초기화(원상품 가격 기본값·전체 선택),
  // 같은 브랜드에서 목록만 refetch(창 포커스)된 경우엔 편집 중인 값을 유지하고 새 행만 추가한다.
  const seededBrandKeyRef = useRef('');
  useEffect(() => {
    const brandChanged = seededBrandKeyRef.current !== itemBrandKey;
    seededBrandKeyRef.current = itemBrandKey;
    setRows((prev) => {
      const next: Record<string, RowEdit> = {};
      brandInfos.forEach((info) => {
        const kept = !brandChanged ? prev[info._id] : undefined;
        next[info._id] = kept ?? rowFromInfo(info, true);
      });
      return next;
    });
    if (brandChanged) setResults({});
  }, [brandInfos, itemBrandKey]);

  const isRowRegistered = (id: string) =>
    registeredInfoIds.has(id) || results[id]?.status === 'success';

  const selectedInfos = useMemo(
    () =>
      brandInfos.filter((info) => rows[info._id]?.checked && !isRowRegistered(info._id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brandInfos, rows, registeredInfoIds, results],
  );

  const availableCount = brandInfos.filter((info) => !isRowRegistered(info._id)).length;
  const allSelected = availableCount > 0 && selectedInfos.length === availableCount;

  const updateRow = (id: string, patch: Partial<RowEdit>) =>
    setRows((prev) => {
      const cur = prev[id];
      if (!cur) return prev;
      return { ...prev, [id]: { ...cur, ...patch } };
    });

  const toggleAll = (checked: boolean) =>
    setRows((prev) => {
      const next = { ...prev };
      brandInfos.forEach((info) => {
        const cur = next[info._id];
        if (cur && !isRowRegistered(info._id)) next[info._id] = { ...cur, checked };
      });
      return next;
    });

  const selectedBugoBrand = useMemo(
    () => bugoBrandList?.find((b: BugoBrand) => b._id === bugoBrandId),
    [bugoBrandList, bugoBrandId],
  );
  const bugoBrandName = selectedBugoBrand?.bugoAgency?.corpName || bugoBrandId;

  const handleSubmit = async () => {
    if (submitting || loadingLists || failedLists) return;
    if (!bugoBrandId) {
      alert('상조 브랜드를 선택해주세요.');
      return;
    }
    if (selectedInfos.length === 0) {
      alert('등록할 상품을 선택해주세요.');
      return;
    }
    if (
      !confirm(
        `${bugoBrandName}에 ${selectedInfos.length}개 상품을 등록합니다.\n등록 후 가격은 상품별 수정 화면에서 바꿀 수 있습니다.`,
      )
    ) {
      return;
    }

    setSubmitting(true);
    setProgress({ done: 0, total: selectedInfos.length });
    const pending: Record<string, RowResult> = {};
    selectedInfos.forEach((info) => {
      pending[info._id] = { status: 'pending' };
    });
    setResults((prev) => ({ ...prev, ...pending }));

    const toItem = (info: GiftItemInfoData): GiftItemBulkCreateItem | null => {
      const row = rows[info._id];
      if (!row) return null;
      return {
        giftItemInfo: info._id,
        priceRegular: row.priceRegular,
        priceRetail: row.priceRetail,
        priceWhole: row.priceWhole,
        priceRebate: row.priceRebate,
        isTaxable: row.isTaxable,
      };
    };
    const items = selectedInfos
      .map(toItem)
      .filter((it): it is GiftItemBulkCreateItem => it !== null);

    let successCount = 0;
    let failCount = 0;
    const finalResults: Record<string, RowResult> = {};

    try {
      const result = await bulkCreateGiftItems({ bugoBrand: bugoBrandId, items }, role);
      // 응답 giftItemInfo는 populate 없이 id 문자열 (혹시 객체로 와도 _id로 정규화)
      const createdIds = new Set(
        result.created.map((c) => {
          const ref = c.giftItemInfo as unknown;
          return typeof ref === 'object' && ref !== null
            ? String((ref as { _id?: string })._id ?? '')
            : String(ref ?? '');
        }),
      );
      const skippedById = new Map(
        result.skipped.map((sk) => [sk.giftItemInfo, sk.reason]),
      );
      items.forEach((it) => {
        if (createdIds.has(it.giftItemInfo)) {
          successCount += 1;
          finalResults[it.giftItemInfo] = { status: 'success' };
        } else {
          failCount += 1;
          const reason = skippedById.get(it.giftItemInfo);
          finalResults[it.giftItemInfo] = {
            status: 'error',
            message: reason ? SKIP_REASON_LABEL[reason] : '등록되지 않음',
          };
        }
      });
      setProgress({ done: items.length, total: items.length });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // 구서버 폴백: 단건 create 순차 호출
        for (const it of items) {
          const createData: GiftItemCreate = {
            ...it,
            bugoBrand: bugoBrandId,
            isTaxable: it.isTaxable ?? false,
          };
          try {
            await createGiftItem(createData, role);
            successCount += 1;
            finalResults[it.giftItemInfo] = { status: 'success' };
          } catch (err) {
            failCount += 1;
            const message =
              axios.isAxiosError(err) &&
              err.response?.status === 400 &&
              err.response.data?.message
                ? String(err.response.data.message)
                : '등록 실패';
            console.error(err);
            finalResults[it.giftItemInfo] = { status: 'error', message };
          }
          setResults((prev) => ({ ...prev, ...finalResults }));
          setProgress((prev) => ({ ...prev, done: prev.done + 1 }));
        }
      } else {
        console.error(error);
        const message =
          axios.isAxiosError(error) &&
          error.response?.status === 400 &&
          error.response.data?.message
            ? String(error.response.data.message)
            : '일괄 등록 요청에 실패했습니다.';
        // 전체 실패: pending 표시 걷어내고 알림
        setResults((prev) => {
          const next = { ...prev };
          items.forEach((it) => delete next[it.giftItemInfo]);
          return next;
        });
        setSubmitting(false);
        alert(message);
        return;
      }
    }

    setResults((prev) => ({ ...prev, ...finalResults }));
    await invalidateQueriesByUrlPart(queryClient, 'gift-item');
    setSubmitting(false);

    if (failCount === 0) {
      alert(`${successCount}개 상품이 등록되었습니다.`);
      navigate('/gift-seller/item');
    } else {
      alert(
        `${successCount}개 등록, ${failCount}개는 건너뛰었습니다. 사유는 표의 상태 열을 확인하세요.`,
      );
    }
  };

  const listStatuses = [giftItemInfoListStatus, bugoBrandListStatus, giftItemListStatus];
  const loadingLists = listStatuses.some(
    (status) => status === 'loading' || status === 'idle',
  );
  const failedLists = listStatuses.includes('error');
  const retry = () =>
    queryClient.invalidateQueries({
      predicate: (query) =>
        String(query.queryKey).includes('gift-item') ||
        String(query.queryKey).includes('bugo-brand'),
    });

  return (
    <div
      className={`${styles.page} min-h-screen bg-gray-50 px-4 py-6 text-gray-900 sm:px-6 lg:px-8 lg:py-8`}
    >
      {/* 등록 중 오버레이 — 서버 처리 동안 화면 조작 차단 */}
      {submitting && (
        <div
          role="status"
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/70"
        >
          <Loading />
          <p className="mt-3 text-sm font-medium text-gray-700">
            {bugoBrandName}에 {progress.total}개 상품 등록 중…
          </p>
          {progress.done > 0 && progress.done < progress.total && (
            <p className="mt-1 text-xs text-gray-500">
              {progress.done} / {progress.total}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-400">창을 닫거나 새로고침하지 마세요.</p>
        </div>
      )}

      <header className="mx-auto mb-6 max-w-6xl">
        <button
          type="button"
          disabled={submitting}
          onClick={() => navigate('/gift-seller/item')}
          className="mb-4 rounded text-sm text-gray-500 hover:text-gray-900 disabled:opacity-40"
        >
          ← 상조별 상품 목록
        </button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">브랜드 일괄 등록</h1>
            <p className="mt-2 text-sm text-gray-500">
              상조와 상품 브랜드를 선택한 뒤, 등록할 상품의 판매 조건을 확인합니다.
            </p>
          </div>
          <Link
            to="/gift-seller/item/new"
            className="rounded border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-700"
          >
            한 개씩 등록
          </Link>
        </div>
      </header>
      <div className="mx-auto max-w-6xl">
        <div className="rounded-md border border-gray-200 bg-white p-5 sm:p-6">
          {failedLists ? (
            <div role="alert" className="py-12 text-center">
              <h2 className="font-semibold">등록에 필요한 목록을 불러오지 못했습니다.</h2>
              <p className="mt-2 text-sm text-gray-500">
                원상품과 상조, 기존 등록 상품을 다시 확인해주세요.
              </p>
              <button
                type="button"
                onClick={retry}
                className="mt-5 rounded border border-gray-300 px-4 py-2 text-sm"
              >
                다시 시도
              </button>
            </div>
          ) : loadingLists ? (
            <div role="status" className="center-box gap-3 py-12">
              <Loading />
              <span className="text-sm text-gray-500">
                등록 정보를 불러오는 중입니다.
              </span>
            </div>
          ) : (
            <>
              <h2 className="mb-5 text-base font-semibold">등록 대상 선택</h2>
              {/* 선택 영역 */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label
                    htmlFor="bulk-agency"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    상조 브랜드 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="bulk-agency"
                    value={bugoBrandId}
                    onChange={(e) => setBugoBrandId(e.target.value)}
                    className="w-full rounded border bg-white px-3 py-2 text-sm disabled:bg-gray-50"
                    disabled={submitting}
                  >
                    <option value="">상조를 선택하세요</option>
                    {bugoBrandList?.map((brand: BugoBrand) => (
                      <option key={brand._id} value={brand._id}>
                        {brand.bugoAgency?.corpName || brand._id}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="bulk-brand"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    상품 브랜드 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="bulk-brand"
                    value={itemBrandKey}
                    onChange={(e) => setItemBrandKey(e.target.value)}
                    className="w-full rounded border bg-white px-3 py-2 text-sm disabled:bg-gray-50"
                    disabled={submitting}
                  >
                    <option value="">가져올 브랜드를 선택하세요</option>
                    {itemBrandOptions.map((opt) => (
                      <option key={opt.key} value={opt.key}>
                        {opt.name} ({opt.count}개)
                      </option>
                    ))}
                  </select>
                  <p className="mt-0.5 text-[10px] text-gray-400">
                    판매 중인 원상품만 표시됩니다.
                  </p>
                </div>
              </div>

              {/* 상품 표 */}
              {itemBrandKey && (
                <div className="mt-6">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                    <div className="text-gray-600">
                      전체{' '}
                      <span className="font-medium text-gray-900">
                        {brandInfos.length}
                      </span>
                      개
                      {bugoBrandId && (
                        <>
                          {' · '}이미 등록{' '}
                          <span className="font-medium text-gray-900">
                            {brandInfos.length - availableCount}
                          </span>
                          개{' · '}선택{' '}
                          <span className="font-medium text-indigo-600">
                            {selectedInfos.length}
                          </span>
                          개
                        </>
                      )}
                    </div>
                    <label className="flex items-center gap-1.5 text-gray-600">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => toggleAll(e.target.checked)}
                        disabled={submitting || availableCount === 0}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      미등록 전체 선택
                    </label>
                  </div>

                  {!bugoBrandId && (
                    <div className="mb-2 rounded bg-yellow-50 p-2 text-xs text-yellow-700">
                      상조 브랜드를 선택하면 이미 등록된 상품이 표시됩니다.
                    </div>
                  )}

                  <div className="rounded border border-gray-200">
                    <table className="block w-full text-sm xl:table">
                      <caption className="sr-only">등록할 원상품과 판매 조건</caption>
                      <thead className="hidden bg-gray-50 text-xs text-gray-600 xl:table-header-group">
                        <tr>
                          <th className="w-8 px-2 py-2"></th>
                          <th className="whitespace-nowrap px-2 py-2 text-left">상품</th>
                          <th className="w-24 whitespace-nowrap px-2 py-2 text-right">
                            정가
                          </th>
                          <th className="w-24 whitespace-nowrap px-2 py-2 text-right">
                            판매가
                          </th>
                          <th className="w-24 whitespace-nowrap px-2 py-2 text-right">
                            도매가
                          </th>
                          <th className="w-24 whitespace-nowrap px-2 py-2 text-right">
                            리베이트
                          </th>
                          <th className="w-14 whitespace-nowrap px-2 py-2 text-center">
                            과세
                          </th>
                          <th className="w-28 whitespace-nowrap px-2 py-2 text-center">
                            상태
                          </th>
                        </tr>
                      </thead>
                      <tbody className="block xl:table-row-group">
                        {brandInfos.map((info) => {
                          const row = rows[info._id];
                          if (!row) return null;
                          const registered = isRowRegistered(info._id);
                          const result = results[info._id];
                          const disabled = submitting || registered;
                          return (
                            <tr
                              key={info._id}
                              className={`grid grid-cols-2 gap-3 border-t p-4 xl:table-row xl:p-0 ${
                                registered ? 'bg-gray-50 text-gray-400' : ''
                              } ${result?.status === 'error' ? 'bg-red-50' : ''}`}
                            >
                              <td className="col-span-2 block xl:table-cell xl:px-2 xl:py-1.5 xl:text-center">
                                <label className="inline-flex items-center gap-2">
                                  <span className="text-xs text-gray-500 xl:hidden">
                                    등록 선택
                                  </span>
                                  <input
                                    aria-label={`${info.name} 등록 선택`}
                                    type="checkbox"
                                    checked={row.checked && !registered}
                                    onChange={(e) =>
                                      updateRow(info._id, { checked: e.target.checked })
                                    }
                                    disabled={disabled}
                                    className="h-4 w-4 rounded border-gray-300"
                                  />
                                </label>
                              </td>
                              <td className="col-span-2 block xl:table-cell xl:px-2 xl:py-1.5">
                                <div className="flex items-center gap-2">
                                  {info.image && typeof info.image === 'object' && (
                                    <img
                                      src={fileBucketUrl(info.image.url, 'raw')}
                                      alt={info.name}
                                      className="h-8 w-8 flex-shrink-0 rounded object-cover"
                                    />
                                  )}
                                  <div className="min-w-0">
                                    <p className="break-words font-medium">
                                      <span className="mr-1 text-[10px] text-gray-400">
                                        [{info.type === 'Delivery' ? '배송' : '쿠폰'}]
                                      </span>
                                      <Link
                                        to={`/gift-seller/item-info/${info._id}/edit`}
                                        className="underline underline-offset-4 hover:text-indigo-700"
                                      >
                                        {info.name}
                                      </Link>
                                    </p>
                                    <p className="text-[10px] text-gray-400">
                                      원상품 정가{' '}
                                      {info.priceRegular?.toLocaleString() || 0} / 판매가{' '}
                                      {info.priceRetail?.toLocaleString() || 0} / 도매가{' '}
                                      {info.priceWhole?.toLocaleString() || 0}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              {NUMBER_FIELDS.map((field) => (
                                <td
                                  key={field}
                                  className="block min-w-0 xl:table-cell xl:px-2 xl:py-1.5"
                                >
                                  <span className="mb-1 block text-xs text-gray-500 xl:hidden">
                                    {PRICE_LABELS[field]}
                                  </span>
                                  <input
                                    aria-label={`${info.name} ${PRICE_LABELS[field]}`}
                                    type="number"
                                    value={row[field] as number}
                                    onChange={(e) =>
                                      updateRow(info._id, {
                                        [field]: Number(e.target.value),
                                      })
                                    }
                                    disabled={disabled}
                                    className="w-full rounded border px-2 py-1 text-right text-sm disabled:bg-gray-100"
                                  />
                                </td>
                              ))}
                              <td className="block xl:table-cell xl:px-2 xl:py-1.5 xl:text-center">
                                <label className="inline-flex items-center gap-2">
                                  <span className="text-xs text-gray-500 xl:hidden">
                                    과세
                                  </span>
                                  <input
                                    aria-label={`${info.name} 과세`}
                                    type="checkbox"
                                    checked={row.isTaxable}
                                    onChange={(e) =>
                                      updateRow(info._id, { isTaxable: e.target.checked })
                                    }
                                    disabled={disabled}
                                    className="h-4 w-4 rounded border-gray-300"
                                  />
                                </label>
                              </td>
                              <td className="block text-xs xl:table-cell xl:px-2 xl:py-1.5 xl:text-center">
                                {result?.status === 'success' ? (
                                  <span className="rounded bg-green-100 px-2 py-0.5 text-green-700">
                                    등록 완료
                                  </span>
                                ) : result?.status === 'error' ? (
                                  <span
                                    className="rounded bg-red-100 px-2 py-0.5 text-red-700"
                                    title={result.message}
                                  >
                                    {result.message ?? '실패'}
                                  </span>
                                ) : result?.status === 'pending' ? (
                                  <span className="text-gray-400">등록 중…</span>
                                ) : registered ? (
                                  <span className="rounded bg-gray-200 px-2 py-0.5 text-gray-600">
                                    이미 등록됨
                                  </span>
                                ) : (
                                  <span className="text-gray-300">미등록</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {brandInfos.length === 0 && (
                          <tr>
                            <td colSpan={8} className="py-8 text-center text-gray-400">
                              이 브랜드에 판매중인 원상품이 없습니다.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {Object.values(results).some((r) => r.status === 'error') && (
                    <div
                      role="alert"
                      className="mt-2 rounded bg-red-50 p-2 text-xs text-red-600"
                    >
                      {Object.entries(results)
                        .filter(([, r]) => r.status === 'error')
                        .map(([id, r]) => {
                          const info = brandInfos.find((i) => i._id === id);
                          return (
                            <p key={id}>
                              {info?.name ?? id}: {r.message}
                            </p>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}

              {/* 제출 */}
              <div className="sticky bottom-0 z-10 mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white py-4">
                <p className="text-xs text-gray-500">
                  {bugoBrandId && selectedInfos.length > 0
                    ? `${bugoBrandName}에 ${selectedInfos.length}개 상품을 등록합니다.`
                    : '상조와 등록할 상품을 선택해주세요.'}
                </p>
                <div className="ml-auto flex gap-2">
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={() => navigate('/gift-seller/item')}
                    className="rounded border border-gray-300 px-4 py-2.5 text-sm text-gray-700 disabled:opacity-40"
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    disabled={submitting || !bugoBrandId || selectedInfos.length === 0}
                    onClick={handleSubmit}
                    className="rounded bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
                  >
                    {submitting ? '등록 중...' : `선택 ${selectedInfos.length}개 등록`}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
